import { assertDefined } from '@listening/utils/ts-utils';
import { useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@tanstack/react-pacer';
import { useEffect, useMemo } from 'react';
import { findDocPackSearchMatches } from '@listening/frontend/doc-pack/search';
import {
  docPackCatalogArgs,
  docPackPageArgs,
  type LoadedDocPack,
} from '@listening/frontend/doc-pack/source';
import { useQueryDataSnapshot } from '@listening/frontend/query/query-cache-subscription';
import { type Eq, useEq } from '@listening/frontend/query/runtime-context';
import { scrollablePrefixSums } from '@/lib/doc-reader/chapter-cache';
import { CHAPTER_CHUNK_SIZE, DocReaderContext } from '@/lib/doc-reader/core';
import { useDocPackUsable } from '@/lib/doc-reader/pack/doc-pack-source';
import { type SearchMatch, useDocSearchStore } from './doc-search-store';

const docPackPageQueryOptions = (
  eq: Eq,
  audioId: string,
  pack: LoadedDocPack,
  page: number,
) => queryOptions(eq.queryOptions(docPackPageArgs(audioId, pack, page)));

const NO_PAGE_KEYS: ReadonlyArray<
  ReturnType<typeof docPackPageQueryOptions>['queryKey']
> = [];

export function useDocSearch() {
  const {
    documentSummary,
    cachedChunks: chaptersData,
    paragraphCounts,
  } = useContextAndErrorIfNull(DocReaderContext);
  const query = useDocSearchStore((s) => s.query);
  const setMatches = useDocSearchStore((s) => s.setMatches);

  const [debouncedQuery] = useDebouncedValue(query, { wait: 300 });
  const searching = debouncedQuery.trim() !== '';
  // The single transport gate (kill-switch flag + content-version check)
  // shared by every pack consumer. Deriving the pack-vs-chapter search
  // decision from anything weaker (e.g. bare isDocPackVersionUsable) would
  // keep pack search "on" while the kill switch has the reader on the legacy
  // path — burning the catalog fetch and returning 0 matches during an
  // incident, with the chapter fallback below unreachable.
  const docPackUsable = useDocPackUsable(documentSummary.doc_pack);
  const eq = useEq();
  const docPackInfo = docPackUsable ? documentSummary.doc_pack : undefined;
  const docPackSearch = searching && !!docPackInfo;
  const docPackCatalogQuery = useQuery({
    ...eq.queryOptions(
      docPackCatalogArgs(documentSummary.id, docPackInfo?.url ?? ''),
    ),
    enabled: docPackSearch,
  });
  const docPack = docPackCatalogQuery.data;
  const docPackPageKeys = useMemo(
    () =>
      docPackSearch && docPack
        ? docPack.catalog.pages.map(
            (page) =>
              docPackPageQueryOptions(eq, documentSummary.id, docPack, page.n)
                .queryKey,
          )
        : NO_PAGE_KEYS,
    [docPack, docPackSearch, documentSummary.id, eq],
  );
  const cachedDocPackPages = useQueryDataSnapshot(docPackPageKeys);
  const loadedDocPackPages = useMemo(
    () => cachedDocPackPages.filter((page) => page !== undefined),
    [cachedDocPackPages],
  );
  // Catalog page numbers (page.n) are copied from source pages at build time
  // and are neither guaranteed contiguous nor 1-based, so a search match's
  // scrollable index must come from the same page.n → array-index map the
  // reader scrolls by — never `page - 1`.
  const docPackPageIndexByN = useMemo(
    () =>
      new Map(
        docPack?.catalog.pages.map((page, idx) => [page.n, idx] as const),
      ),
    [docPack],
  );

  // Chapter search reads whatever chunks the reader has in its query cache,
  // through the provider's cache view rather than one observer per chunk: an
  // observer would keep every chunk alive past the reader's retention time.
  const prefixSums = useMemo(
    () => scrollablePrefixSums(paragraphCounts),
    [paragraphCounts],
  );

  useEffect(() => {
    const trimmed = debouncedQuery.trim().toLowerCase();
    if (!trimmed) {
      setMatches([], { loaded: 0, total: 0 });
      return;
    }

    const queryWords = trimmed.split(/\s+/);
    const matches: Array<SearchMatch> = [];

    if (docPackSearch) {
      const docPackMatches = findDocPackSearchMatches(
        loadedDocPackPages,
        trimmed,
      ).flatMap((match): SearchMatch[] => {
        const scrollableIdx = docPackPageIndexByN.get(match.page);
        // A match on a page missing from the catalog can't be scrolled to;
        // drop it rather than jump to a wrong (or invalid) index.
        if (scrollableIdx === undefined) return [];
        return [
          {
            type: 'doc-pack',
            page: match.page,
            charStart: match.word.chars[0],
            charEnd: match.word.chars[1],
            scrollableIdx,
          },
        ];
      });
      setMatches(docPackMatches, {
        loaded: loadedDocPackPages.length,
        total: docPack?.catalog.pageCount ?? 0,
      });
      return;
    }

    let loadedChapterCount = 0;

    for (const [chunkIdx, chunkData] of chaptersData.entries()) {
      if (!chunkData) continue;
      loadedChapterCount += chunkData.length;
      for (const [chapterInChunkIdx, chapter] of chunkData.entries()) {
        const chapterIdx = chunkIdx * CHAPTER_CHUNK_SIZE + chapterInChunkIdx;
        for (const [paragraphIdx, paragraph] of chapter.paragraphs.entries()) {
          for (const [sentenceIdx, sentence] of paragraph.sentences.entries()) {
            if (queryWords.length === 1) {
              for (const [wordIdx, word] of sentence.words.entries()) {
                if (word.text.toLowerCase().includes(trimmed)) {
                  matches.push({
                    chapterIdx,
                    paragraphIdx,
                    sentenceIdx,
                    wordIdx,
                    word,
                    scrollableIdx:
                      assertDefined(prefixSums[chapterIdx]) + 1 + paragraphIdx,
                  });
                }
              }
            } else {
              for (
                let wIdx = 0;
                wIdx <= sentence.words.length - queryWords.length;
                wIdx++
              ) {
                const wordsSlice = sentence.words.slice(
                  wIdx,
                  wIdx + queryWords.length,
                );
                const allMatch = queryWords.every((qw, i) =>
                  assertDefined(wordsSlice[i]).text.toLowerCase().includes(qw),
                );
                if (allMatch) {
                  for (const [offset, word] of wordsSlice.entries()) {
                    matches.push({
                      chapterIdx,
                      paragraphIdx,
                      sentenceIdx,
                      wordIdx: wIdx + offset,
                      word,
                      scrollableIdx:
                        assertDefined(prefixSums[chapterIdx]) +
                        1 +
                        paragraphIdx,
                    });
                  }
                }
              }
            }
          }
        }
      }
    }

    setMatches(matches, {
      loaded: loadedChapterCount,
      total: documentSummary.chapters.length,
    });
  }, [
    debouncedQuery,
    chaptersData,
    docPackSearch,
    docPack?.catalog.pageCount,
    docPackPageIndexByN,
    loadedDocPackPages,
    prefixSums,
    setMatches,
    documentSummary.chapters.length,
  ]);
}
